// src/components/VideosSection.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { Link } from "react-router-dom";

// Utilitaire: construit une miniature à partir d'un videoId
const thumbFromId = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

// Utilitaire: construit l'URL du lecteur "privacy enhanced"
const playerFromId = (id) =>
  `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`;

export default function VideosSection({
  limit = 6,            // combien de vidéos montrer (Home)
  showHeader = true,    // afficher le titre de section
  showSeeAll = false,   // montrer le bouton "Voir tout" (-> /videos)
  className = "",       // styles extérieurs optionnels
}) {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null); // videoId ouvert dans la modale

  // Chargement temps réel depuis Firestore (collection "videos")
  useEffect(() => {
    let unsub;
    (async () => {
      const { db } = await import("../firebase"); // import dynamique pour éviter les cycles
      const q = query(collection(db, "videos"), orderBy("createdAt", "desc"));
      unsub = onSnapshot(q, (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data() || {};
          const videoId = data.videoId || d.id;
          return {
            id: d.id,
            videoId,
            title: data.title || "",
            thumbnail: data.thumbnail || thumbFromId(videoId),
            createdAt: data.createdAt || null,
          };
        });
        setVideos(list);
      });
    })();
    return () => unsub && unsub();
  }, []);

  // Limite d’éléments pour la Home
  const display = useMemo(() => {
    return limit ? videos.slice(0, limit) : videos;
  }, [videos, limit]);

  return (
    <section className={`bg-black text-white py-14 ${className}`}>
      {showHeader && (
        <div className="flex items-end justify-between px-6 md:px-12 mb-6">
          <h2 className="text-2xl md:text-3xl font-bold">Dernières vidéos</h2>
          {showSeeAll && (
            <Link
              to="/videos"
              className="text-sm uppercase tracking-wide text-gray-300 hover:text-white"
            >
              Voir tout
            </Link>
          )}
        </div>
      )}

      {/* État vide */}
      {display.length === 0 && (
        <p className="text-center text-gray-400 px-6">
          Videos en preparation.
        </p>
      )}

      {/* Grille des vignettes */}
      <div className="px-6 md:px-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {display.map((v) => (
          <button
            key={v.id}
            onClick={() => setActive(v.videoId)}
            className="group text-left"
            aria-label={`Lire la vidéo ${v.title || v.videoId}`}
          >
            <div className="relative rounded-xl overflow-hidden bg-zinc-900 aspect-video">
              <img
                src={v.thumbnail}
                alt={v.title || "Miniature YouTube"}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
              {/* Bouton Play */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-white/90 group-hover:bg-white transition grid place-items-center shadow">
                  <div className="ml-0.5 border-l-8 border-y-transparent border-y-[10px] border-y-transparent border-l-black" />
                </div>
              </div>
            </div>
            {v.title && (
              <p className="mt-2 text-sm text-gray-300 line-clamp-2">{v.title}</p>
            )}
          </button>
        ))}
      </div>

      {/* Modale lecteur */}
      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-5xl aspect-video rounded-xl overflow-hidden bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Iframe chargé seulement quand la modale est ouverte (lazy) */}
            <iframe
              src={playerFromId(active)}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full"
            />
            <button
              onClick={() => setActive(null)}
              className="absolute -top-10 right-0 md:top-4 md:right-4 bg-white/10 hover:bg-white/20 text-white rounded-full px-3 py-1 md:px-4 md:py-2"
              aria-label="Fermer"
            >
              Fermer ✕
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
